import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { detectUserFlow } from '@tink/tink-link-core/src/analytics-user-flow';

import { AppState } from '../../stores/redux-store';
import { Analytics, TinkLinkAnalytics } from './tink-link-analytics';

export type AnalyticsProps = {
  tinklinkAnalytics: Analytics;
};

export const AnalyticsContext = React.createContext<AnalyticsProps>({
  tinklinkAnalytics: new TinkLinkAnalytics(),
});

export type AnalyticsProviderProps = PropsFromRedux & {
  children: ReactNode;
  initialTinklinkAnalytics: Analytics;
};

const AnalyticsProviderPresenter: React.FC<AnalyticsProviderProps> = ({
  user,
  children,
  credentials,
  handoffSession,
  session,
  initialTinklinkAnalytics,
}) => {
  const [tinklinkAnalytics] = useState(initialTinklinkAnalytics);

  useEffect(() => {
    tinklinkAnalytics.update({
      credentialsId: credentials?.id,
      providerName: credentials?.providerName,
    });
  }, [tinklinkAnalytics, credentials]);

  useEffect(() => {
    const userId = user?.id;

    if (userId) {
      tinklinkAnalytics.update({ userId });
    }
  }, [tinklinkAnalytics, user]);

  useEffect(() => {
    tinklinkAnalytics.update({
      flow: detectUserFlow(window.location.href),
    });
  }, [tinklinkAnalytics]);

  useEffect(() => {
    if (handoffSession) {
      tinklinkAnalytics.update({ sessionId: handoffSession.sessionId });
    }
  }, [handoffSession, tinklinkAnalytics]);

  useEffect(() => {
    if (session) {
      tinklinkAnalytics.update({
        sessionSourceAccountSupplied: Boolean(session.sourceAccountNumber),
      });

      if (session.merchantId) {
        tinklinkAnalytics.update({ merchantId: session.merchantId });
      }
    }
  }, [session, tinklinkAnalytics]);

  return (
    <AnalyticsContext.Provider value={{ tinklinkAnalytics }}>{children}</AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    credentials: state.credentials.data,
    handoffSession: state.handoffSession.data,
    user: state.user.user,
    session: state.session.data,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const AnalyticsProvider = connector(AnalyticsProviderPresenter);

export const useAnalytics = () => useContext(AnalyticsContext);
